/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, createContext, type ForwardedRef, forwardRef, type HTMLAttributes, isValidElement } from 'react';
import { type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconButtonContext, type IconButtonProps } from '../icon-button/icon-button.component';
import { IconContext, type IconProps } from "../icon/icon.component";
import { Text, TextContext, type TextProps } from "../text/text.component";

interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'style' | 'className' | 'color'> {
  slot?: string;
  /** The size of the badge. (Default: 20) */
  size?: keyof typeof sizeVariants;
  /** The variant of the badge. (Default: blueLight) */
  color?: keyof typeof fillColorVariants;
  /** Whether the badge is rounded. (Default: false) */
  isRounded?: boolean;
  /** Whether the badge is pastel. (Default: false) */
  isPastel?: boolean;
  style?: stylex.StyleXStyles;
}

const BadgeContext = createContext<ContextValue<BadgeProps, HTMLSpanElement>>({});

const Badge = forwardRef((props: BadgeProps, ref: ForwardedRef<HTMLSpanElement>) => {
  [props, ref] = useContextProps(props, ref, BadgeContext);

  const {
    size = 20,
    color = 'blueLight',
    isPastel = false,
    isRounded = false,
    style,
    children,
    ...otherProps
  } = props;

  const isTextOnly = Children.toArray(children).every(c => !isValidElement(c));

  return (
    <span
      {...otherProps}
      role="presentation"
      ref={ref}
      {...stylex.props(
        styles.base,
        isRounded && styles.rounded,
        sizeVariants[size],
        isPastel ? pastelColorVariants[color] : fillColorVariants[color],
        style
      )}>
      <Provider values={[
        [
          TextContext,
          {
            size: textSizes[size],
            color: textColors[isPastel ? 'pastel' : 'fill'][color],
            style: badgeTextStyles.base,
          },
        ],
        [
          IconContext,
          {
            slot: null!,
            color: iconColors[isPastel ? 'pastel' : 'fill'][color],
            size: iconSizes[size],
          }
        ],
        [
          IconButtonContext,
          {
            slot: null!,
            size: badgeSizeToIconButtonSize[size],
            variant: 'ghost',
            style: {
              base: iconButtonBaseStyles.base,
              icon: iconButtonIconColorStyles[isPastel ? 'pastel' : 'fill'][color],
            },
          } satisfies IconButtonProps,
        ]
      ]}>
        {typeof props.children === 'string' || isTextOnly ? <Text>{children}</Text> : children}
      </Provider>
    </span>
  );
});

const iconSizes: Record<keyof typeof sizeVariants, IconProps['size']> = {
  16: 8,
  20: 12,
  32: 12,
};

const textSizes: Record<keyof typeof sizeVariants, TextProps['size']> = {
  16: 10,
  20: 12,
  32: 13,
};

const textColors: {
  fill: Record<keyof typeof fillColorVariants, TextProps['color']>,
  pastel: Record<keyof typeof pastelColorVariants, TextProps['color']>
} = {
  fill: {
    red: 'white',
    yellow: 'white',
    orange: 'white',
    green: 'white',
    turquoise: 'white',
    blueLight: 'white',
    blueDark: 'white',
    violet: 'white',
    greyDark: 'inverted',
    greyLight: 'inverted',
    disabled: 'disabled',
  },
  pastel: {
    red: 'critical',
    yellow: 'yellow',
    orange: 'orange',
    green: 'positive',
    turquoise: 'turquoise',
    blueLight: 'blueLight',
    blueDark: 'blueDark',
    violet: 'violet',
    greyDark: 'secondary',
    greyLight: 'quaternary',
    disabled: 'disabled',
  },
};

const styles = stylex.create({
  base: {
    borderRadius: 4,
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    width: 'fit-content',
  },
  rounded: {
    borderRadius: 999,
  }
});

const sizeVariants = stylex.create({
  16: {
    gap: 2,
    height: 16,
    paddingBottom: 3.5,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 2.5,
  },
  20: {
    gap: 4,
    height: 20,
    paddingBottom: 4,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 4,
  },
  32: {
    gap: 4,
    height: 32,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 8,
  },
});

const fillColorVariants = stylex.create({
  red: { backgroundColor: $.surfaceCritical },
  yellow: { backgroundColor: $.accentSun60 },
  orange: { backgroundColor: $.accentPumpkin60 },
  green: { backgroundColor: $.surfacePositive },
  turquoise: { backgroundColor: $.accentTurquoise60 },
  blueLight: { backgroundColor: $.accentOcean60 },
  blueDark: { backgroundColor: $.accentSky60 },
  violet: { backgroundColor: $.accentPlum60 },
  greyDark: { backgroundColor: $.opacity48 },
  greyLight: { backgroundColor: $.opacity32 },
  disabled: { backgroundColor: $.buttonContentDisabled },
});

const pastelColorVariants = stylex.create({
  red: { backgroundColor: $.surfaceCriticalSilent },
  yellow: { backgroundColor: $.accentSun10 },
  orange: { backgroundColor: $.accentPumpkin10 },
  green: { backgroundColor: $.surfacePositiveMedium },
  turquoise: { backgroundColor: $.accentTurquoise10 },
  blueLight: { backgroundColor: $.accentOcean10 },
  blueDark: { backgroundColor: $.accentSky10 },
  violet: { backgroundColor: $.accentPlum10 },
  greyDark: { backgroundColor: $.opacity08 },
  greyLight: { backgroundColor: $.opacity06 },
  disabled: { backgroundColor: $.buttonContentDisabled },
});

const iconColors: {
  fill: Record<keyof typeof fillColorVariants, IconProps['color']>,
  pastel: Record<keyof typeof pastelColorVariants, IconProps['color']>
} = {
  fill: {
    red: 'white',
    yellow: 'white',
    orange: 'white',
    green: 'white',
    turquoise: 'white',
    blueLight: 'white',
    blueDark: 'white',
    violet: 'white',
    greyDark: 'inverted',
    greyLight: 'inverted',
    disabled: 'disabled',
  },
  pastel: {
    red: 'critical',
    yellow: 'yellow',
    orange: 'orange',
    green: 'positive',
    turquoise: 'turquoise',
    blueLight: 'blueLight',
    blueDark: 'blueDark',
    violet: 'violet',
    greyDark: 'secondary',
    greyLight: 'quaternary',
    disabled: 'disabled',
  },
};

const badgeTextStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  }
});

const badgeSizeToIconButtonSize: Record<keyof typeof sizeVariants, IconButtonProps['size']> = {
  16: 16,
  20: 20,
  32: 32,
};

const iconButtonBaseStyles = stylex.create({
  base: {
    borderStyle: 'none',
    paddingInline: 0,
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': null,
    },
    cursor: 'pointer',
    height: 'auto',
    width: 'auto',
  },
});

const iconButtonIconColorStyles: {
  fill: Record<keyof typeof fillColorVariants, stylex.StyleXStyles>,
  pastel: Record<keyof typeof pastelColorVariants, stylex.StyleXStyles>,
} = {
  fill: stylex.create({
    blueLight: { fill: $.accentOcean80, stroke: $.accentOcean80 },
    blueDark: { fill: $.accentSky80, stroke: $.accentSky80 },
    disabled: { fill: $.buttonContentDisabled, stroke: $.buttonContentDisabled },
    green: { fill: $.iconWhite, stroke: $.iconWhite },
    greyDark: { fill: $.iconInverted, stroke: $.iconInverted },
    greyLight: { fill: $.iconInverted, stroke: $.iconInverted },
    orange: { fill: $.iconWhite, stroke: $.iconWhite },
    red: { fill: $.iconWhite, stroke: $.iconWhite },
    turquoise: { fill: $.iconWhite, stroke: $.iconWhite },
    violet: { fill: $.iconWhite, stroke: $.iconWhite },
    yellow: { fill: $.iconWhite, stroke: $.iconWhite },
  }),
  pastel: stylex.create({
    blueLight: { fill: $.accentOcean80, stroke: $.accentOcean80 },
    blueDark: { fill: $.accentSky80, stroke: $.accentSky80 },
    disabled: { fill: $.buttonContentDisabled, stroke: $.buttonContentDisabled },
    green: { fill: $.iconPositive, stroke: $.iconPositive },
    greyDark: { fill: $.iconSecondary, stroke: $.iconSecondary },
    greyLight: { fill: $.iconQuaternary, stroke: $.iconQuaternary },
    orange: { fill: $.accentPumpkin80, stroke: $.accentPumpkin80 },
    red: { fill: $.colorRed50, stroke: $.colorRed50 },
    turquoise: { fill: $.accentTurquoise80, stroke: $.accentTurquoise80 },
    violet: { fill: $.accentPlum60, stroke: $.accentPlum60 },
    yellow: { fill: $.accentSun80, stroke: $.accentSun80 },
  }),
};

export { Badge, BadgeContext, type BadgeProps };
